import { Link } from "react-router-dom";
import { Magnet, MapPin, Smartphone } from "lucide-react";
import { stepsForPlatform, techFor } from "@/data/site";
import { IMAGES } from "@/data/products";
import { usePlatform } from "@/context/PlatformContext";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/Primitives";

const ICONS = [Magnet, Smartphone, MapPin];

const HowItWorks = () => {
  const { platform, config } = usePlatform();
  const steps = stepsForPlatform(platform);
  const tech = techFor(platform);
  const phoneWord = platform === "android" ? "Android phone" : "iPhone";

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="scroll-mt-24 px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl rounded-[2rem] border border-black/5 bg-tracksy-cream px-5 py-10 sm:rounded-[2.5rem] sm:px-10 sm:py-14 lg:px-16 lg:py-20">
        <div className="grid grid-cols-1 items-center gap-10 sm:gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="How it works" title="Attach. Connect. Find." desc={tech.subscription} />
            <div className="mt-8 space-y-7 sm:mt-10 sm:space-y-8">
              {steps.map((s, i) => {
                const Icon = ICONS[i];
                return (
                  <Reveal key={s.step} delay={i * 0.12}>
                    <div data-testid={`how-step-${i + 1}`} className="flex gap-5">
                      <div className="flex flex-col items-center">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white font-heading text-sm font-extrabold shadow-subtle sm:h-12 sm:w-12" style={{ color: "var(--brand-accent)" }}>{s.step}</span>
                        {i < steps.length - 1 && <span className="mt-2 w-px flex-1 bg-black/10" />}
                      </div>
                      <div className="pb-2">
                        <p className="flex items-center gap-2 font-heading text-base font-bold uppercase tracking-wide text-tracksy-ink sm:text-lg">
                          <Icon className="h-4 w-4" style={{ color: "var(--brand-accent)" }} />
                          {s.title}
                        </p>
                        <p className="mt-1.5 text-sm leading-relaxed text-tracksy-sub">{s.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.3}>
              <p className="mt-7 rounded-2xl border border-black/5 bg-white p-4 text-xs leading-relaxed text-tracksy-sub sm:mt-8">
                <span className="font-bold text-tracksy-ink">Good to know: </span>
                {tech.notGps}{" "}
                <Link to="/how-it-works" data-testid="how-it-works-learn-more" className="font-bold hover:underline" style={{ color: "var(--brand-accent)" }}>Read how Tracksy works →</Link>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <div className="relative overflow-hidden rounded-[1.5rem] shadow-lift sm:rounded-[2rem]">
                <img
                  src={platform === "android" ? IMAGES.handAndroid : IMAGES.hand}
                  alt={`Tracksy magnetic tracker next to ${phoneWord} showing live ${config.networkShort} locations`}
                  className="aspect-[4/3] w-full object-cover sm:aspect-[4/5]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 rounded-full bg-white/85 px-3.5 py-2 text-[11px] font-bold text-tracksy-ink backdrop-blur-xl sm:bottom-5 sm:left-5 sm:right-auto sm:px-4 sm:text-xs">
                  {tech.compat}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
